import React, { useEffect, useState, useRef } from 'react';
import { MotionConfig } from 'framer-motion';
import { useAppStore } from './app/hooks/useAppStore';
import { useAssessmentSession } from './assessment/hooks/useAssessmentSession';
import { useAppNavigation } from './app/hooks/useAppNavigation';
import { usePracticeHandlers } from './practice/hooks/usePracticeSession';
import { getAllQuestions, type ModuleId, type Question } from './content';
import { isSessionExpired, findAccountByLinkCode, generateClassroomId, type Classroom } from './accounts';
import { AuthGuard } from './app/AuthGuard';
import { AppContent } from './app/AppContent';
import { assertSupportCoverage } from './support';
import { warn, error as logError } from './utils/logger';
import { SESSION_CHECK_INTERVAL_MS } from './config/constants';
import { dataLayer } from './data/dataLayer';
// Safe question loading with error handling
let QUESTIONS: Question[] = [];
try {
  QUESTIONS = getAllQuestions();
  if (QUESTIONS.length === 0) warn('No questions loaded - check question data files');
  if (process.env.NODE_ENV === 'development') assertSupportCoverage(QUESTIONS);
} catch (error) {
  logError('Failed to load questions:', error);
  QUESTIONS = [];
}
export function App() {
  const store = useAppStore();
  const {
    state,
    accounts,
    activeAccount,
    selectedGroup,
    setSelectedGroup,
    startingPeriod,
    manualPeriod,
    setStartingPeriod,
    recordAnswer,
    markCheatSheetViewed,
    clearPendingLevelUp,
    updateProfileDisplayName,
    switchAccount,
    isInitialized,
    loadDemoAccounts,
    currentLearnerDisplayName,
    requiresViewingLearnerSelection,
    getLearnerData,
    getLearnerModuleMastery,
    viewingLearnerId,
    setViewingLearner
  } = store;
  // Load reducedMotion preference from dataLayer (D5)
  const [reducedMotionPref, setReducedMotionPref] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    dataLayer.getLearnerPreferences().then(prefs => {
      if (!cancelled) setReducedMotionPref(prefs.reducedMotion);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized,
    switchAccount,
    setViewingLearner,
    loadDemoAccounts,
    clearActiveAccount: store.clearActiveAccount
  });
  const practice = usePracticeHandlers({
    state,
    selectedGroup,
    markCheatSheetViewed,
    enterPracticeView: nav.enterPracticeView,
    resetToDashboard: nav.resetToDashboard,
    resetToLearningLab: nav.resetToLearningLab
  });
  const assessment = useAssessmentSession({
    familyMastery: state.familyMastery || {},
    selectedGroup,
    recordAnswer,
    allQuestions: QUESTIONS,
    isReady: isInitialized && !nav.showWelcome,
    learnerId: viewingLearnerId || activeAccount?.id
  });
  // ── Session Timeout Enforcement (Spec 8.3) ─────────────────────────
  const activeAccountRef = useRef(activeAccount);
  activeAccountRef.current = activeAccount;
  useEffect(() => {
    if (!isInitialized || nav.showWelcome) return;
    if (nav.currentNavView === 'login' || nav.currentNavView === 'register') return;
    const checkSessionTimeout = () => {
      const acct = activeAccountRef.current;
      if (acct && acct.lastActive) {
        const expired = isSessionExpired(acct.lastActive, acct.role);
        if (expired) nav.handleLogout();
      }
    };
    checkSessionTimeout();
    const interval = setInterval(checkSessionTimeout, SESSION_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isInitialized, activeAccount?.id, activeAccount?.role, nav.showWelcome, nav.currentNavView]);
  // ── Assessment handlers ──────────────────────────────────────────
  const handleStartAssessment = (config: import('./assessment/types').AssessmentConfig) => {
    const result = assessment.startAssessment(config);
    if (result) practice.startFocusSession(result.questions);
  };
  const handleAssessmentComplete = () => {
    // Capture assignmentId before completeAssessment clears the state
    const assignmentId = assessment.assessmentAttempt?.assignmentId;
    assessment.completeAssessment();
    // Mark assignment as completed by this student so it won't reappear
    if (assignmentId && store.viewingLearnerId) {
      dataLayer.markAssignmentCompletedByStudent(assignmentId, store.viewingLearnerId).catch(e => logError('Failed to mark assignment completed', e));
    } else if (assignmentId && activeAccount?.role === 'learner' && activeAccount?.id) {
      dataLayer.markAssignmentCompletedByStudent(assignmentId, activeAccount.id).catch(e => logError('Failed to mark assignment completed', e));
    }
    nav.resetToDashboard();
    nav.setCurrentNavView('assessmentReport');
  };
  const handleResumeAssessment = () => {
    const result = assessment.resumeAssessment();
    if (result) practice.startFocusSession(result.questions);
  };
  // ── Render ─────────────────────────────────────────────────────────
  return <MotionConfig reducedMotion={reducedMotionPref ? 'always' : 'user'} data-id="element-757">
      <AuthGuard showWelcome={nav.showWelcome} currentNavView={nav.currentNavView} accounts={accounts} activeAccount={activeAccount} onLoadDemo={nav.handleLoadDemoAccounts} onNavigateToLogin={nav.handleNavigateToLogin} onNavigateToRegister={nav.handleNavigateToRegister} onLoginSuccess={nav.handleLoginSuccess} onBack={() => nav.setShowWelcome(true)} createAccount={store.createAccount} onJoinClassroom={joinCode => {
      const result = store.joinClassroomByCode(activeAccount?.id || '', joinCode);
      if (result) {
        return {
          success: true
        };
      }
      return {
        success: false,
        error: 'Koppelcode niet gevonden. Controleer de code en probeer opnieuw.'
      };
    }} onLinkChild={linkCode => {
      const found = findAccountByLinkCode(accounts, linkCode);
      if (found && activeAccount) {
        store.linkParentToLearnerById(activeAccount.id, found.id);
        return {
          success: true
        };
      }
      return {
        success: false,
        error: 'Koppelcode niet gevonden. Vraag de code aan de docent van je kind.'
      };
    }} onCreateClassroom={(name, grade) => {
      if (activeAccount) {
        const classroom: Classroom = {
          id: generateClassroomId(),
          name,
          groupLevel: grade,
          learnerIds: [],
          createdAt: Date.now(),
          teacherId: activeAccount.id,
          joinCode: '' // Auto-generated by addClassroom()
        };
        store.addClassroom(activeAccount.id, classroom);
      }
    }} onJoinExistingClassroom={(classroomId, mode) => {
      if (activeAccount) {
        if (mode === 'transfer') {
          store.transferClassroomOwnership(classroomId, activeAccount.id);
        } else {
          store.joinClassroomAsCoTeacher(activeAccount.id, classroomId);
        }
      }
    }} onCompleteOnboarding={() => store.completeOnboarding()} data-id="element-758">
        <AppContent questions={QUESTIONS} state={state} accounts={accounts} activeAccount={activeAccount} selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} startingPeriod={startingPeriod} manualPeriod={manualPeriod} setStartingPeriod={setStartingPeriod} recordAnswer={recordAnswer} markCheatSheetViewed={markCheatSheetViewed} clearPendingLevelUp={clearPendingLevelUp} updateProfileDisplayName={updateProfileDisplayName} currentLearnerDisplayName={currentLearnerDisplayName} requiresViewingLearnerSelection={requiresViewingLearnerSelection} getLearnerData={getLearnerData} getLearnerModuleMastery={getLearnerModuleMastery} viewingLearnerId={viewingLearnerId} nav={nav} practice={practice} assessment={assessment} handleStartAssessment={handleStartAssessment} handleAssessmentComplete={handleAssessmentComplete} handleResumeAssessment={handleResumeAssessment} data-id="element-759" />
      </AuthGuard>
    </MotionConfig>;
}